import { IRequestParams, IResponse } from '@dash/apiInstance';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import Api, { executeAction } from 'src/helpers/api';
import { logger } from '../../../../helpers/logger';
import { t } from '../../../../i18n';
import notifications from '../../../../redux/notifications/actions';
import actionTypes from './actionTypes';
import { BulkUploadWorkersRequestAction, ProcessActionRequestAction } from './actions.d';
import { IProcessActionResponse } from './types';
import actions from './actions';

const bulkUploadWorkersApi = new Api('workers/import');

type ProcessActionData =
  | {
      fileType?: string;
      [key: string]: any;
    }
  | undefined;

export function* processAction({ payload }: ProcessActionRequestAction<{}, ProcessActionData>) {
  try {
    const { url, method, data, key } = payload;

    const result: IResponse<IProcessActionResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      executeAction,
      { url, method, data },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200) {
      if (key === 'create_form_data') {
        // @ts-ignore
        yield put(actions.processActionSuccess({ formData: result?.data?.success?.data, key }));
      } else if (key === 'create_worker') {
        yield put(actions.processActionSuccess({ key }));
        yield put(actions.clearState());

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.created_successfully', {
              item: t('dash.talent.bulk_upload_workers.modal.bulk_upload_tab.worker'),
            }),
          }),
        );
      } else {
        yield put(actions.processActionSuccess({ key }));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: result?.messages?.join(' ') || '',
          }),
        );
      }
    } else if (result?.data && key === 'template') {
      // @ts-ignore
      const { fileBlobUrl, fileName } = result.data;
      const link = document.createElement('a');

      link.href = fileBlobUrl;
      link.target = '_blank';
      link.download = fileName || 'workers_template.csv';
      link.click();

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.exported_successfully', {
            item: t('dash.talent.bulk_upload_workers.modal.bulk_upload_tab.workers_template_button_title'),
          }),
        }),
      );

      yield put(actions.processActionSuccess({ key }));
    } else {
      yield put(actions.processActionError({ errors: result, key }));

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* bulkUploadAction({ payload }: BulkUploadWorkersRequestAction) {
  const { form } = payload;
  const data = new FormData();

  if (form.file.value !== null) {
    data.append('csv_file', form.file.value);
  }

  try {
    // @ts-ignore
    const result = yield call(
      sendRequest,
      bulkUploadWorkersApi.multipleParamsSendFormData,
      { data },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      yield put(actions.bulkUploadWorkersSuccess());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.talent.bulk_upload_workers.modal.bulk_upload_tab.bulk_upload_success_message'),
        }),
      );
    } else {
      yield put(actions.bulkUploadWorkersError());
      yield put(actions.clearState());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actionTypes.PROCESS_ACTION_REQUEST, processAction),
    takeEvery(actionTypes.BULK_UPLOAD_WORKERS_REQUEST, bulkUploadAction),
  ]);
}
